<template>
  <div class="editor-wrapper inline">
    <div id="trf-toolbar-text-editor" :class="{'is-disabled': disabled}">
      <label :class="{ labelObrigatorio: isTextEditorVazio && isObrigatorio }">
        {{ label }}
      </label>
      <Editor
        @keydown.capture="tratarLineBreak"
        @paste="onPaste"
        tinymceScriptSrc="/tinymce/tinymce.min.js"
        output-format="html"
        v-model="content"
        @selectionChange="$emit('changeText', content, this), verificarSeTextEditorEstaVazio()"
        ref="editorRef"
        :id="id"
        :disabled="disabled"
        :tag-name="tag"
        :init="{
          selector: tag,
          inline: true,
          resize: false,
          statusbar: false,
          language: 'pt_BR',
          menubar: false,
          height: pHeight,
          promotion: false,
          toolbar:
            toolbarOptions,
          toolbar_location: 'bottom',
          toolbar_mode: 'sliding',
          toolbar_persist: toolbarPersist,
          font_family_formats:
            'Serif=serif; Sans=sans; Arial=arial,helvetica,sans-serif; Arial Black=arial black; Courier=courier; Courier New=courier new; Arial Black; Comic Sans MS=comic sans ms; Helvetica=helvetica; Impact=impact; Lucida Grande=lucida grande; Lucida Sans=lucida sans; Tahoma=tahoma; Times=times; Times New Roman=times new roman; Verdana=verdana',
          fontsize_formats: '8pt 10pt 12pt 14pt 16pt 18pt 24pt',
          content_style: editorStyle,
          fixed_toolbar_container: customToolbar? `#toolbar-for-${id}` : undefined,
          paste_preprocess: (editor, args) => pastePreProcess(editor, args),
      }"
        @init="$emit('ready', false), toolbarTextEditorIsLoaded()"
      />
      <div :id='`toolbar-for-${id}`' class="custom-toolbar"></div>
    </div>
    <span class="tip-label is-size-7">{{ tipLabel }}</span>
    <div class="mensagemDeErro mt-1">
      <span v-if="isTextEditorVazio && isObrigatorio" class="campo-obrigatório">Campo obrigatório.</span>
    </div>
  </div>
</template>

<script>
import Editor from "@tinymce/tinymce-vue";

const CONTROL_KEYS = [
  'Tab', 'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'
];

export default {
  name: "TrfToolbarTextEditorInline",
  components: {
    Editor,
  },
  props: {
    tipLabel: String,
    label: String,
    tag: {
      type: String,
      default: 'p'
    },
    isObrigatorio: Boolean,
    id: String,
    disabled: {
      type: [Boolean],
      default: false
    },
    editorStyle: {
      type: String,
      default: 'body { font-size: 12pt; color: #555 }',
      required: true
    },
    defaultText: {
      type: String,
      default: "Título formulário",
      required: false
    },
    pHeight: {
      type: String,
      default: "36px",
      required: false
    },
    maxlength: {
      type: Number,
      default: 90
    },
    toolbarOptions: {
      type: String,
      default: 'bold italic underline '
    },
    toolbarPersist: {
      type: Boolean,
      default: false
    },
    customToolbar: {
      type: Boolean,
      default: false
    },
  },
  emits: ["changeText", "ready"],
  data() {
    return {
      content: this.defaultText,
      isTextEditorVazio: false,
    };
  },
  methods: {
    getValueAsText() {
      return tinyMCE.get(this.id)?.contentAreaContainer.textContent?.trim();
    },
    pastePreProcess(e,args) {
      if (args.content && args.content.match(/<img[^>]*?\/?>/i)) {
        args.content = "";
        return;
      }
    },
    onPaste(e) {
      const clipboardData = e.clipboardData || window.clipboardData;
      if (!clipboardData) return;

      const textoColado = clipboardData.getData('text/plain');
      const tamanhoTextoAtual = tinymce.activeEditor.getContent({ format: "text" }).length;
      
      const tamanhoTextoTotal = tamanhoTextoAtual + textoColado.length;
      if (tamanhoTextoTotal <= this.maxlength) return;

      e.preventDefault();
      const sobra = this.maxlength - tamanhoTextoAtual;
      if (sobra == 0) return;

      tinymce.activeEditor.insertContent(textoColado.substring(0, sobra));
    },
    tratarLineBreak(event) {
      const isControlOrMetaKey = event.ctrlKey || event.metaKey;

      const length = this.getValueAsText().length;
      const isEnter = event.keycode == 13 || event.key == "Enter";
      const naoDeveInserirCaracter = length >= this.maxlength && !CONTROL_KEYS.includes(event.key) && !isControlOrMetaKey;
      
      if (isEnter || naoDeveInserirCaracter) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    iniciarTextEditorComValor(value) {
      this.content = value
      return this.getValueAsText();
    },
    limparCampo() {
      this.content = ""
      return this.content
    },
    toolbarTextEditorIsLoaded() {
      document.dispatchEvent(
        new CustomEvent('trf-toolbar-text-editor-is-ready', {
          detail: true,
          composed: true
        })
      );
    },
    verificarSeTextEditorEstaVazio() {
      let validation = false;
      if(this.getValueAsText() != null) {
        validation = this.getValueAsText() == ""
      } else {
        // para o método acima funcionar o componente precisa de um id único e valido.
        // então é adicionada uma segunda validação menos precisa.
        validation = this.content == "" || this.content == null
      }
      this.isTextEditorVazio = validation;
      return this.isTextEditorVazio
    },
    async focusAndSelect(transitionTime = 350) {
      await this.$nextTick(); // espera próximo tick do vue para garantir que o componente esteja carregado.
      const editorInstance = tinyMCE.get(this.id);
      if (editorInstance) {
        await new Promise((resolve) => {setTimeout(() => {resolve()}, transitionTime)}); //espera tempo padrão de animação do css, evita calculo errado da posição de elementos;
        editorInstance.focus();
        editorInstance.selection.select(tinyMCE.activeEditor.getBody(), true);
        tinyMCE.activeEditor.ui.show();
      } else { // Proteção para tentar replicar comportamento se editor não tiver ID, porém não é tão preciso
        await new Promise((resolve) => {setTimeout(() => {resolve()}, transitionTime)}); //espera tempo padrão de animação do css;
        this.$refs.editorRef.$el.focus();
        tinyMCE.activeEditor.selection.select(tinyMCE.activeEditor.getBody(), true);
        tinyMCE.activeEditor.ui.show();
      }
    }
  },
};
</script>

<style lang="scss">
.editor-wrapper.inline #trf-toolbar-text-editor .mce-content-body {
  word-break: break-word;
  line-break: auto;
  white-space: break-spaces;
}
.custom-toolbar {
  max-height: 0px;
  overflow: hidden;
  transition: max-height 0.35s ease-in 0s;
  .tox-tinymce {
    border: 0 none!important;
    border-radius: 0px!important;
  }
}
.mce-edit-focus:focus + .custom-toolbar {
  max-height:50px;
  transition: max-height 0.35s ease-out 0s;
}
#trf-toolbar-text-editor {
  width:100%;
  .tox-tinymce {
    border: 1px solid #ccc;
    border-radius: 4px;
  }

  .tox:not(.tox-tinymce-inline) .tox-editor-header {
    border-bottom: 1px solid #ccc;
  }

  .tox .tox-tbtn {
    border-bottom-left-radius: 3px;
    height: 28px;
    border: 1px solid #ccc;
    width: 30px;
    margin-right: 5px;
  }

  .tox .tox-toolbar__group {
    padding: 0 0px;
  }

  div.tox-toolbar__primary .tox-toolbar__group:first-child  .tox-tbtn:first-child, .tox-toolbar__overflow .tox-toolbar__group:first-child .tox-tbtn:first-child {
    margin-left: 9.5px ;
  }

  .tox .tox-toolbar,
  .tox .tox-toolbar__overflow,
  .tox .tox-toolbar__primary {
    background-image: unset;
  }

  .tox .tox-tbtn--select {
    width: auto;
  }
}
.is-disabled .tox-edit-area {
  cursor: not-allowed!important;
  pointer-events: none!important;
  * {
    cursor: not-allowed!important;
    pointer-events: none!important;
  }
  iframe {
    background: hsl(0deg, 0%, 96%)!important;
    border-color: hsl(0deg, 0%, 96%)!important;
    color: hsl(0deg, 0%, 48%)!important;
  }
}
</style>

<style lang="scss" scoped>
.inValida {
  border: 1px solid #a94442;
}
.mensagemDeErro {
  margin: 0 0 10px 0;
  color: #a94442;
  font-size: 12px;
}
.labelObrigatorio {
  color: #a94442;
}
</style>
